import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { CronScheduleBuilderHandle } from '@/components/cron';
import { useGetConfigTemplatesQuery } from '@/features/transformers/api/transformersApi';
import { useGetCurrentUserQuery } from '@/features/users/api/usersApi';
import type { CreateTriggerRequest, ScheduleSummary, ScheduleType } from '@/types/schedule';
import { getApiErrorMessage } from '@/utils/apiErrors';

import { useAdvancedCronMigration } from '../hooks/useAdvancedCronMigration';
import { openedForCronMigration } from '../utils/advancedCronTimezoneBootstrap';
import { buildScheduleTriggerRequest } from '../utils/buildScheduleTriggerRequest';
import { analyzeLegacyCronMigration } from '../utils/legacyCronMigration';
import {
  DEFAULT_CRON_EXPRESSION,
  DEFAULT_DAILY_HOUR,
  DEFAULT_DAILY_MINUTE,
  DEFAULT_REPEATING_FREQUENCY,
  DEFAULT_REPEATING_FREQUENCY_UNIT,
  LEGACY_DAILY_TIMEZONE_FALLBACK,
  getDefaultCronTimeZone,
  getDefaultDailyTimeZone,
} from '../utils/scheduleDefaults';
import { isScheduleConfigValid } from '../utils/scheduleFormSchema';
import { convertUTCToLocalTime, getBrowserTimezone } from '../utils/timezoneData';
import { CronScheduleMigrationConfirmDialog } from './CronScheduleMigrationConfirmDialog';
import { ScheduleConfigPanel } from './ScheduleConfigPanel';

interface EditScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTriggerRequest) => Promise<void>;
  onDelete: () => Promise<void>;
  schedule: ScheduleSummary | null;
}

interface FormState {
  scheduleType: ScheduleType;
  daily: {
    hour: number;
    minute: number;
    timeZone: string;
  };
  repeating: {
    frequency: number;
    frequencyUnit: 'minute' | 'hour';
    startingAt: { hour: number; minute: number } | null;
    timeZone: string;
  };
  cron: {
    expression: string;
    explain: string;
    timeZone: string;
    isDirty?: boolean;
    isValid?: boolean;
    canonicalExpression?: string;
  };
  templateName: string;
}

function getInitialFormState(schedule: ScheduleSummary | null): FormState {
  const defaultState: FormState = {
    scheduleType: 'daily',
    daily: {
      hour: DEFAULT_DAILY_HOUR,
      minute: DEFAULT_DAILY_MINUTE,
      timeZone: getDefaultDailyTimeZone(),
    },
    repeating: {
      frequency: DEFAULT_REPEATING_FREQUENCY,
      frequencyUnit: DEFAULT_REPEATING_FREQUENCY_UNIT,
      startingAt: null,
      timeZone: getDefaultDailyTimeZone(),
    },
    cron: {
      expression: DEFAULT_CRON_EXPRESSION,
      explain: '',
      timeZone: getDefaultCronTimeZone(),
      isDirty: false,
    },
    templateName: '',
  };

  if (!schedule) {
    return defaultState;
  }

  // Extract template name if present
  const templateName = schedule.templateName || '';

  // Determine schedule type from the schedule data
  if (schedule.dailySchedule) {
    const [hourStr, minuteStr] = schedule.dailySchedule.timeUTC.split(':');
    const utcHour = parseInt(hourStr, 10);
    const utcMinute = parseInt(minuteStr, 10);
    const tz = schedule.dailySchedule.timeZone?.trim() || LEGACY_DAILY_TIMEZONE_FALLBACK;
    // Convert stored UTC time back to local time for the form
    const local = convertUTCToLocalTime(utcHour, utcMinute, tz);
    return {
      ...defaultState,
      scheduleType: 'daily',
      daily: {
        hour: local.hour,
        minute: local.minute,
        timeZone: tz,
      },
      templateName,
    };
  }

  if (schedule.repeatingSchedule) {
    const storedTimeZone = schedule.repeatingSchedule.timeZone?.trim() || '';
    const displayTz = storedTimeZone || getBrowserTimezone();
    let startingAt: { hour: number; minute: number } | null = null;
    if (schedule.repeatingSchedule.startingAt) {
      const utcHour = parseInt(schedule.repeatingSchedule.startingAt.split(':')[0], 10);
      const utcMinute = parseInt(schedule.repeatingSchedule.startingAt.split(':')[1], 10);
      const local = convertUTCToLocalTime(utcHour, utcMinute, displayTz);
      startingAt = { hour: local.hour, minute: local.minute };
    }
    return {
      ...defaultState,
      scheduleType: 'repeating',
      repeating: {
        frequency: schedule.repeatingSchedule.frequency,
        frequencyUnit: schedule.repeatingSchedule.frequencyUnit as 'minute' | 'hour',
        startingAt,
        timeZone: storedTimeZone,
      },
      templateName,
    };
  }

  if (schedule.cronExpression) {
    const storedTz = schedule.cronTimeZone?.trim() || getDefaultCronTimeZone();
    return {
      ...defaultState,
      scheduleType: 'cron',
      cron: {
        expression: schedule.cronExpression,
        explain: schedule.cronExplain || '',
        timeZone: storedTz,
        isDirty: false,
        canonicalExpression: schedule.cronExpression,
      },
      templateName,
    };
  }

  // Default with template name
  return {
    ...defaultState,
    templateName,
  };
}

function isTemplateOnlyChange(current: FormState, initial: FormState): boolean {
  if (current.templateName === initial.templateName) {
    return false;
  }
  if (current.scheduleType !== initial.scheduleType) {
    return false;
  }

  switch (current.scheduleType) {
    case 'cron':
      return (
        !current.cron.isDirty &&
        current.cron.expression === initial.cron.expression &&
        current.cron.explain === initial.cron.explain
      );
    case 'daily':
      return (
        current.daily.hour === initial.daily.hour &&
        current.daily.minute === initial.daily.minute &&
        current.daily.timeZone === initial.daily.timeZone
      );
    case 'repeating':
      return (
        current.repeating.frequency === initial.repeating.frequency &&
        current.repeating.frequencyUnit === initial.repeating.frequencyUnit &&
        current.repeating.timeZone === initial.repeating.timeZone &&
        current.repeating.startingAt?.hour === initial.repeating.startingAt?.hour &&
        current.repeating.startingAt?.minute === initial.repeating.startingAt?.minute
      );
    default:
      return false;
  }
}

export const EditScheduleDialog = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  schedule,
}: EditScheduleDialogProps) => {
  const [formState, setFormState] = useState<FormState>(() => getInitialFormState(schedule));
  const initialFormStateRef = useRef<FormState>(getInitialFormState(schedule));
  const [repeatingLegacyNoTimeZone, setRepeatingLegacyNoTimeZone] = useState(
    () => !!schedule?.repeatingSchedule?.startingAt && !schedule.repeatingSchedule.timeZone?.trim()
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMigrationConfirm, setShowMigrationConfirm] = useState(false);
  const cronBuilderRef = useRef<CronScheduleBuilderHandle>(null);

  // Get current user to check if tech-support
  const { data: currentUser } = useGetCurrentUserQuery();
  const isTechSupport = currentUser?.role === 'tech-support';

  // Fetch templates only for tech-support users
  const { data: templatesData } = useGetConfigTemplatesQuery(undefined, {
    skip: !isTechSupport,
  });

  // Extract and sort template names
  const availableTemplates = useMemo(() => {
    if (!templatesData || !Array.isArray(templatesData)) return [];
    return (templatesData as Array<{ Name?: string; name?: string }>)
      .map((t) => t.Name || t.name || '')
      .filter((name) => name.length > 0)
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [templatesData]);

  // Reset form when schedule changes
  useEffect(() => {
    if (schedule) {
      const initial = getInitialFormState(schedule);
      initialFormStateRef.current = initial;
      setFormState(initial);
      setRepeatingLegacyNoTimeZone(
        !!schedule.repeatingSchedule?.startingAt && !schedule.repeatingSchedule.timeZone?.trim()
      );
    }
  }, [schedule]);

  useLayoutEffect(() => {
    if (!open) {
      setShowMigrationConfirm(false);
      setShowDeleteConfirm(false);
      setError(null);
    }
  }, [open]);

  const handleClose = () => {
    setError(null);
    setShowDeleteConfirm(false);
    setShowMigrationConfirm(false);
    onClose();
  };

  // Prevent closing on backdrop click or escape key
  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    handleClose();
  };

  const handleScheduleTypeChange = (type: ScheduleType) => {
    setFormState((prev) => ({ ...prev, scheduleType: type }));
  };

  const handleDailyChange = (daily: FormState['daily']) => {
    setFormState((prev) => ({ ...prev, daily }));
  };

  const handleRepeatingChange = (repeating: FormState['repeating']) => {
    if (repeating.timeZone.trim()) {
      setRepeatingLegacyNoTimeZone(false);
    }
    setFormState((prev) => ({ ...prev, repeating }));
  };

  const handleCronChange = (cron: FormState['cron']) => {
    setFormState((prev) => ({ ...prev, cron }));
  };

  const handleTemplateChange = (templateName: string) => {
    setFormState((prev) => ({ ...prev, templateName }));
  };

  const migration = useAdvancedCronMigration({
    schedule,
    scheduleType: formState.scheduleType,
    cronTimeZone: formState.cron.timeZone,
    cronExpression: formState.cron.canonicalExpression ?? formState.cron.expression,
  });

  const cronLegacyMigration = useMemo(() => {
    const storedCron = schedule?.cronExpression?.trim();
    if (
      !storedCron ||
      formState.scheduleType !== 'cron' ||
      formState.cron.isDirty ||
      migration.isMigrationActive
    ) {
      return null;
    }
    return analyzeLegacyCronMigration(storedCron, schedule?.cronTimeZone);
  }, [
    migration.isMigrationActive,
    formState.cron.isDirty,
    formState.scheduleType,
    schedule?.cronExpression,
    schedule?.cronTimeZone,
  ]);

  const buildTriggerRequest = useCallback(
    (options?: { confirmLegacyMigration?: boolean }): CreateTriggerRequest => {
      if (!schedule) {
        throw new Error('No schedule to edit');
      }

      const { scheduleType, daily, repeating, cron, templateName } = formState;

      return buildScheduleTriggerRequest({
        mode: 'edit',
        scheduleType,
        daily,
        repeating,
        cron,
        cronBuilderRef,
        baseIds: {
          baseid: parseInt(String(schedule.baseId), 10),
          environmentId: parseInt(String(schedule.environmentId), 10),
        },
        templateName: templateName || undefined,
        storedCronExplain: schedule.cronExplain || undefined,
        repeatingLegacyNoTimeZone,
        confirmLegacyMigration: options?.confirmLegacyMigration,
        openedForCronMigration: schedule ? openedForCronMigration(schedule) : false,
        storedCronTimeZone: schedule.cronTimeZone?.trim() || undefined,
        storedCronTimeFrame: schedule.cronTimeFrame ?? undefined,
      });
    },
    [formState, schedule, repeatingLegacyNoTimeZone]
  );

  const isFormValid = isScheduleConfigValid(
    formState.scheduleType,
    formState.daily,
    formState.repeating,
    formState.cron,
    { repeatingLegacyNoTimeZone }
  );

  const handleSubmit = async () => {
    if (!isFormValid) {
      setError('Please complete all required schedule fields before saving.');
      return;
    }

    if (
      cronLegacyMigration?.needsMigration &&
      !isTemplateOnlyChange(formState, initialFormStateRef.current)
    ) {
      setShowMigrationConfirm(true);
      return;
    }

    await submitSchedule();
  };

  const submitSchedule = async (options?: { confirmLegacyMigration?: boolean }) => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (formState.scheduleType === 'cron') {
        const built = cronBuilderRef.current?.buildForSave();
        if (built?.userEdited && !built?.isValid) {
          setError('Please fix the cron schedule before saving.');
          return;
        }
      }

      const request = buildTriggerRequest(options);
      await onSubmit(request);

      handleClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update schedule'));
    } finally {
      setIsSubmitting(false);
      setShowMigrationConfirm(false);
    }
  };

  const handleConfirmLegacyMigration = () => {
    void submitSchedule({ confirmLegacyMigration: true });
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    try {
      setIsDeleting(true);
      setError(null);
      await onDelete();
      handleClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete schedule'));
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!schedule) return null;

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle>Edit Schedule</DialogTitle>

      <DialogContent dividers>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Schedule Info */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              Recipe:
            </Typography>
            <Chip label={schedule.transformerName} size="small" color="primary" />
            <Typography variant="body2" color="text.secondary">
              Environment:
            </Typography>
            <Chip label={schedule.environmentName} size="small" color="secondary" />
            {schedule.isSchedulesPaused && <Chip label="Paused" size="small" color="warning" />}
          </Stack>
        </Box>

        {/* Template Selector (Tech-Support Only) */}
        {isTechSupport && availableTemplates.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              value={formState.templateName || null}
              onChange={(_event, newValue) => handleTemplateChange(newValue || '')}
              options={availableTemplates}
              getOptionLabel={(option) => option.toUpperCase()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template (Optional)"
                  placeholder="Select a template..."
                  size="small"
                />
              )}
              disabled={isSubmitting || isDeleting}
              clearOnEscape
              size="small"
              sx={{ maxWidth: 300 }}
            />
          </Box>
        )}

        {/* Schedule Type + Config */}
        <ScheduleConfigPanel
          scheduleType={formState.scheduleType}
          onScheduleTypeChange={handleScheduleTypeChange}
          daily={formState.daily}
          onDailyChange={handleDailyChange}
          repeating={formState.repeating}
          onRepeatingChange={handleRepeatingChange}
          cron={formState.cron}
          onCronChange={handleCronChange}
          cronBuilderRef={cronBuilderRef}
          repeatingLegacyNoTimeZone={repeatingLegacyNoTimeZone}
          cronMigration={{
            editableScope: migration.editableScope,
            timeframeUnconfirmed: migration.timeframeUnconfirmed,
            showStoredUtcExpression: migration.showStoredUtcExpression,
            onConfirmTimezone: migration.confirmTimezone,
            onConfirmTimeframe: migration.confirmTimeframe,
            onTimezoneMigrationChange: migration.notifyTimezoneChanged,
          }}
          storedCronTimeZone={schedule.cronTimeZone}
          storedCronTimeFrame={schedule.cronTimeFrame}
          showLegacyMigrationNotice={Boolean(schedule?.cronExpression)}
          disabled={isSubmitting || isDeleting}
        />

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            Are you sure you want to delete this schedule? This action cannot be undone.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant={showDeleteConfirm ? 'contained' : 'outlined'}
          color="error"
          onClick={handleDelete}
          disabled={isSubmitting || isDeleting}
        >
          {showDeleteConfirm ? 'Confirm Delete' : 'Delete'}
        </Button>

        <Stack direction="row" spacing={1}>
          {showDeleteConfirm && (
            <Button
              variant="outlined"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel Delete
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            onClick={handleClose}
            disabled={isSubmitting || isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || isDeleting || !isFormValid || !migration.readyToSave}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </DialogActions>

      <CronScheduleMigrationConfirmDialog
        open={open && showMigrationConfirm}
        migration={cronLegacyMigration}
        onCancel={() => setShowMigrationConfirm(false)}
        onConfirm={handleConfirmLegacyMigration}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
};
